

'use client';

import React, { useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Search, X, Edit, Trash2, Loader2, UploadCloud } from "lucide-react";
import { debounce } from "lodash";
import * as XLSX from "xlsx";
import { Info } from "lucide-react";

// 🔹 shadcn/ui
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "react-hot-toast";

// 🔹 API
import {
  useGetAllLeadsQuery,
  useDeleteLeadByIdMutation,
} from "@/features/api/leadsApi";



const InfoIcon = ({ text }) => (
  <span
    className="ml-1 inline-flex items-center"
    onClick={(e) => e.stopPropagation()}
  >
    <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-pointer peer" />

    {/* Tooltip */}
    <span className="fixed z-[9999] hidden peer-hover:block bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap translate-y-[-8px] translate-x-[8px]">
      {text}
    </span>
  </span>
);


function LeadSearchFilter({
  searchTerm,
  onSearchChange,
  onReset,
  startDate,
  endDate,
  onDateChange,
}) {
  const debouncedChange = useRef(
    debounce((value) => onSearchChange(value), 300)
  ).current;

  const handleInputChange = useCallback(
    (e) => debouncedChange(e.target.value),
    [debouncedChange]
  );


  return (
    <div className="flex flex-col md:flex-row gap-4 items-center w-full">
      <div className="relative w-full md:max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by First Name, Last Name, Email, Location..."
          defaultValue={searchTerm}
          onChange={handleInputChange}
          className="pl-10"
        />
      </div>

      <div className="flex gap-2 items-center">
        <Input
          type="date"
          value={startDate}
          onChange={(e) => onDateChange("start", e.target.value)}
        />
        <span className="text-sm text-muted-foreground">to</span>
        <Input
          type="date"
          value={endDate}
          onChange={(e) => onDateChange("end", e.target.value)}
        />
      </div>

      <Button onClick={onReset} variant="ghost" className="flex gap-2">
        <X className="h-4 w-4" /> Reset
      </Button>
    </div>
  );
}

/* =========================================================================
   Helpers
   ========================================================================= */
const normalizePhone = (phone = "") => phone.toString().replace(/\D/g, "");
const normalizeEmail = (email = "") => (email || "").trim().toLowerCase();
const normalizeName = (first = "", last = "") =>
  `${(first || "").trim().toLowerCase()} ${(last || "").trim().toLowerCase()}`.trim();

/* =========================================================================
   Main Component
   ========================================================================= */
export default function LeadListPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "descending",
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgressText, setUploadProgressText] = useState("");

  const navigate = useNavigate();

  // Fetch leads
  const { data, isLoading, isFetching, isError, error, refetch } = useGetAllLeadsQuery(
    {
      search: searchTerm || undefined,
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction === "ascending" ? "asc" : "desc",
    },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const [deleteLead, { isLoading: isDeleting }] = useDeleteLeadByIdMutation();
  const allLeads = Array.isArray(data?.leads) ? data.leads : [];

  /* =========================================================================
     Filtering + Sorting
     ========================================================================= */
  const filteredLeads = useMemo(() => {
    let leads = [...allLeads];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      leads = leads.filter((lead) =>
        [lead.firstName, lead.lastName, lead.email, lead.location, lead.service, lead.address].some((f) =>
          f?.toString().toLowerCase().includes(q)
        )
      );
    }
    if (startDate || endDate) {
      leads = leads.filter((lead) => {
        if (!lead.createdAt) return false;
        const leadDate = new Date(lead.createdAt);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start && leadDate < start) return false;
        if (end && leadDate > end) return false;
        return true;
      });
    }
    return leads;
  }, [searchTerm, startDate, endDate, allLeads]);

  const sortedLeads = useMemo(() => {
    const items = [...filteredLeads];
    return items.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (sortConfig.key === "createdAt") {
        return sortConfig.direction === "ascending"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "ascending"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(bVal);
      }
      return 0;
    });
  }, [filteredLeads, sortConfig]);

  const handleRequestSort = (key) => {
    setSortConfig((prev) =>
      prev?.key === key && prev.direction === "ascending"
        ? { key, direction: "descending" }
        : { key, direction: "ascending" }
    );
  };


const handleExportExcel = () => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10);
  const timeStr = now.toTimeString().slice(0, 5).replace(":", "-");
  const fileName = `leads_${dateStr}_${timeStr}.xlsx`;

  const ws = XLSX.utils.json_to_sheet(
    sortedLeads.map((lead) => ({
      "First Name": lead.firstName ?? "",
      "Last Name": lead.lastName ?? "",
      Gender: lead.gender ?? "Unknown",
      Email: lead.email ?? "",
      Phone: lead.phone ?? "",
      Service: lead.service ?? "Unknown",
      Location: lead.location ?? "Unknown",
      Address: lead.address ?? "Unknown",
      Status: lead.status ?? "Unknown",
      Date: lead.createdAt
        ? new Date(lead.createdAt).toLocaleDateString()
        : new Date().toLocaleDateString(),
    }))
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Leads");
  XLSX.writeFile(wb, fileName, { compression: true });
};

  /* =========================================================================
     Delete
     ========================================================================= */
  const handleDelete = async (id) => {
    if (!id) return;
    if (confirm("Are you sure you want to delete this lead?")) {
      try {
        await deleteLead(id).unwrap();
        toast.success("Lead deleted ✅");
        if (refetch) refetch();
      } catch {
        toast.error("Failed to delete ❌");
      }
    }
  };

  /* =========================================================================
     Upload + Sheet Parsing
     ========================================================================= */
  const parseFile = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: "" });
  };


const parseExcelDateSafely = (value) => {
  if (!value) return null;

  // Excel serial number (e.g. 45231)
  if (typeof value === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    return new Date(excelEpoch.getTime() + value * 86400000).toISOString();
  }

  // Normal date string
  const parsed = new Date(value);
  if (!isNaN(parsed)) {
    return parsed.toISOString();
  }

  // Fallback → keep original
  return null;
};


const mapRowToLead = (row) => {
  const get = (keys) => {
    for (const k of keys) {
      const found = Object.keys(row).find(
        (rk) => rk.toLowerCase().trim() === k.toLowerCase().trim()
      );
      if (found) return row[found];
    }
    return "";
  };

  const rawDate = get(["date", "createdAt", "created at"]);

  return {
    firstName: get(["firstName", "firstname", "first name"])?.toString().trim() || "",
    lastName: get(["lastName", "lastname", "last name"])?.toString().trim() || "",
    gender: get(["gender", "sex"])?.toString().trim() || "Unknown",
    email: get(["email"])?.toString().trim().toLowerCase() || "",
    phone: get(["phone", "mobile", "contact"])?.toString().trim() || "",
    service: get(["service"])?.toString().trim() || "Unknown",
    location: get(["location", "city", "place"])?.toString().trim() || "Unknown",
    address: get(["address"])?.toString().trim() || "Unknown",
    status: get(["status"])?.toString().trim() || "Unknown",
    createdAt: parseExcelDateSafely(rawDate),
  };
};




  const uploadToBackend = async (leadsArray) => {
    try {
      const res = await fetch("https://fetc.in/api/v1/lead/upload-bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leads: leadsArray }),
      });
      if (!res.ok) throw new Error("Bulk upload failed");
      return await res.json();
    } catch (err) {
      console.error("Upload failed:", err);
      throw err;
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    setUploadProgressText("Parsing file...");
    try {
      const rows = await parseFile(file);
      if (!rows.length) throw new Error("No data found in file");

      const parsed = rows.map(mapRowToLead);
      const unique = [];
      const existing = new Set(
        allLeads.map(
          (l) =>
            `${normalizeName(l.firstName, l.lastName)}|${normalizeEmail(
              l.email
            )}|${normalizePhone(l.phone || "")}`
        )
      );

      for (const lead of parsed) {
        const key = `${normalizeName(lead.firstName, lead.lastName)}|${normalizeEmail(
          lead.email
        )}|${normalizePhone(lead.phone)}`;
        if (!existing.has(key)) {
          existing.add(key);
          unique.push(lead);
        }
      }

      if (unique.length === 0) {
        toast("No new leads to upload (duplicates skipped).");
        setUploading(false);
        return;
      }

      setUploadProgressText(`Uploading ${unique.length} leads...`);
      await uploadToBackend(unique);
      toast.success(`✅ Uploaded ${unique.length} leads`);
      if (refetch) refetch();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      setUploadProgressText("");
    }
  };

  const onFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleFileUpload(file);
    e.target.value = "";
  };

  /* =========================================================================
     Render
     ========================================================================= */
  return (
    <div className="flex-1 p-6 bg-background min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl font-semibold">👥 Lead Management</h2>
        <p className="text-muted-foreground mt-1 text-lg">
          View, search, upload, filter, and export all leads.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <CardTitle>All Leads</CardTitle>
            <CardDescription>
              Manage your leads with upload, search, and export.
            </CardDescription>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={handleExportExcel} variant="outline" className="flex gap-2">
              <Download className="h-4 w-4" /> Export Excel
            </Button>

            <label className="inline-block">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onFileInputChange}
                className="hidden"
                id="lead-upload-input"
              />
              <Button
                onClick={() => document.getElementById("lead-upload-input").click()}
                variant="outline"
                className="flex gap-2"
                disabled={uploading}
              >
                <UploadCloud className="h-4 w-4" /> Upload
              </Button>
            </label>

            <Button onClick={() => navigate("/admin/leads/create")}>
              + New Lead
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <LeadSearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onReset={() => {
              setSearchTerm("");
              setStartDate("");
              setEndDate("");
            }}
            startDate={startDate}
            endDate={endDate}
            onDateChange={(type, value) =>
              type === "start" ? setStartDate(value) : setEndDate(value)
            }
          />

          <Separator className="my-4" />

          {uploading && (
            <div className="p-2 mb-2 rounded bg-muted/40 text-sm">
              {uploadProgressText || "Uploading..."}
            </div>
          )}

          {isError ? (
            <p className="p-4 text-red-500">
              {error?.data?.message || "Failed to fetch leads"}
            </p>
          ) : isLoading || isFetching ? (
            <div className="flex items-center justify-center gap-2 p-4">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading leads...
            </div>
          ) : sortedLeads.length === 0 ? (
            <p className="p-4 text-muted-foreground">📭 No leads found</p>
          ) : (
            <Table>
        <TableCaption>List of registered leads</TableCaption>
                <TableHeader>
          <TableRow>
            {[
              ["First Name", "firstName", "Client / user first name"],
              ["Last Name", "lastName", "Client / user last name"],
              ["Gender", "gender", "Client / user gender"],
              ["Email", null, "Client / user email address"],
              ["Phone", null, "Client / user phone number"],
              ["Service", "service", "Service taken by the client"],
              ["Location", "location", "Office location"],
              ["Address", "address", "Client / user address"],
              ["Status", "status", "Lead current status"],
              ["Date", "createdAt", "Lead received date"],
            ].map(([label, key, tip]) => (
              <TableHead
                key={label}
                onClick={key ? () => handleRequestSort(key) : undefined}
                className={`${
                  key ? "cursor-pointer" : ""
                }`}
              >
                <div className="flex items-center gap-1">
                  <span>{label}</span>
                  <InfoIcon text={tip} />
                </div>
              </TableHead>
            ))}

            <TableHead className="text-right">
              <div className="flex items-center justify-end gap-1">
                <span>Actions</span>
                <InfoIcon text="Edit or delete lead" />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>



              <TableBody>
                {sortedLeads.map((lead) => (
                  <TableRow key={lead._id}>
                    <TableCell>{lead.firstName ?? "Unnamed"}</TableCell>
                    <TableCell>{lead.lastName ?? ""}</TableCell>
                    <TableCell>{lead.gender ?? "Unknown"}</TableCell>
                    <TableCell>{lead.email ?? "No Email"}</TableCell>
                    <TableCell>{lead.phone ?? "N/A"}</TableCell>
                  <TableCell>{lead.service ?? "Unknown"}</TableCell>
                  <TableCell>{lead.location ?? "Unknown"}</TableCell>
                  <TableCell>{lead.address ?? "Unknown"}</TableCell>
                  <TableCell>{lead.status ?? "Unknown"}</TableCell>

                   <TableCell>
                      {lead.createdAt
                        ? new Date(lead.createdAt).toLocaleDateString()
                        : "—"}
                    </TableCell>

                    <TableCell className="text-right flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => navigate(`/admin/leads/edit/${lead._id}`)}
                      >
                        <Edit size={18} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDelete(lead._id)}
                        disabled={isDeleting}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

